import type {
  Camera,
  DateFilterPreset,
  ModuleCatalogEntry,
  RoleListItem,
  SaveRolePermissionsPayload,
  UserCameraOption,
  UserListItem,
  UserRoleOption,
} from "../types.tsx";
import { fetchCameras as fetchCamerasFromApi } from "../../../api/cameras.ts";
import { ApiRequestError } from "../api/client.tsx";
import {
  adminCreateRole,
  adminCreateUser,
  adminDeleteRole,
  adminDeleteUser,
  adminFetchActiveRoleOptions,
  adminFetchRolesList,
  adminFetchUsersList,
  adminGetRole,
  adminGetUser,
  adminListRoleRecords,
  adminPatchRoleStatus,
  adminPatchUserStatus,
  adminReplaceRolePermissions,
  adminUpdateRole,
  adminUpdateUser,
  mapApiRoleToListItem,
  mapApiUserToListItem,
  resolveModuleCatalog,
  roleOptionsFromApiRoles,
} from "../api/adminUsersRolesClient.ts";

type SortDir = "asc" | "desc";

function startOfDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function inDateFilter(iso: string, preset: DateFilterPreset, customFrom?: string, customTo?: string): boolean {
  if (preset === "all") return true;
  const t = new Date(iso).getTime();
  const now = new Date();
  const sod = startOfDay(now);
  if (preset === "custom") {
    if (!customFrom && !customTo) return true;
    const from = customFrom ? new Date(customFrom + "T00:00:00").getTime() : -Infinity;
    const to = customTo ? new Date(customTo + "T23:59:59.999").getTime() : Infinity;
    return t >= from && t <= to;
  }
  if (preset === "today") return t >= sod && t < sod + 86400000;
  if (preset === "yesterday") return t >= sod - 86400000 && t < sod;
  if (preset === "last7") return t >= now.getTime() - 7 * 86400000;
  if (preset === "last30") return t >= now.getTime() - 30 * 86400000;
  if (preset === "thisWeek") {
    const dow = now.getDay();
    const mon = sod - (dow === 0 ? 6 : dow - 1) * 86400000;
    return t >= mon;
  }
  if (preset === "thisMonth") {
    const m = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    return t >= m;
  }
  return true;
}

export async function fetchCameras(search: string): Promise<Camera[]> {
  const rows = await fetchCamerasFromApi(search.trim() || undefined);
  return rows.map((c) => ({ id: c.id, name: c.name }));
}

export type FetchUsersParams = {
  page: number;
  limit: number;
  search?: string;
  roles?: string[];
  status?: "active" | "inactive";
  dateFilter: DateFilterPreset;
  dateFrom?: string;
  dateTo?: string;
  sortBy: "role" | "name" | "email" | "createdAt" | "status";
  sortDir: SortDir;
  favoritesOnly?: boolean;
  favoriteUserIds?: string[];
};

export async function fetchActiveRoleOptions(): Promise<UserRoleOption[]> {
  return adminFetchActiveRoleOptions();
}

export async function fetchUsers(
  p: FetchUsersParams,
): Promise<{
  items: UserListItem[];
  roleOptions: UserRoleOption[];
  cameraOptions: UserCameraOption[];
  total: number;
}> {
  if (p.favoritesOnly) {
    const fav = p.favoriteUserIds ?? [];
    if (fav.length === 0) return { items: [], roleOptions: [], cameraOptions: [], total: 0 };

    let roleNameById = new Map<string, string>();
    let roleOptions: UserRoleOption[] = [];
    try {
      const roleRecords = await adminListRoleRecords();
      const allRoleItems = roleRecords.map((r) => mapApiRoleToListItem(r));
      const mapped = roleOptionsFromApiRoles(allRoleItems);
      roleNameById = mapped.roleNameById;
      roleOptions = mapped.roleOptions;
    } catch {
      /* role filter options optional for favorites */
    }

    const users = await Promise.all(fav.map((id) => adminGetUser(id)));
    let rows = users.filter((u): u is NonNullable<typeof u> => u != null).map((u) => mapApiUserToListItem(u, roleNameById));

    const q = (p.search ?? "").trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.email.toLowerCase().includes(q) ||
          (r.fullName ?? "").toLowerCase().includes(q) ||
          r.id.replace(/-/g, "").toLowerCase().includes(q.replace(/\s/g, "")),
      );
    }
    if (p.roles && p.roles.length > 0) {
      const set = new Set(p.roles);
      rows = rows.filter((r) => r.businessRoleId && set.has(r.businessRoleId));
    }
    if (p.status === "active") rows = rows.filter((r) => r.isActive);
    if (p.status === "inactive") rows = rows.filter((r) => !r.isActive);
    rows = rows.filter((r) => inDateFilter(r.createdAt, p.dateFilter, p.dateFrom, p.dateTo));

    const dir = p.sortDir === "desc" ? -1 : 1;
    rows.sort((a, b) => {
      let cmp = 0;
      if (p.sortBy === "name") cmp = (a.fullName ?? "").localeCompare(b.fullName ?? "");
      else if (p.sortBy === "email") cmp = a.email.localeCompare(b.email);
      else if (p.sortBy === "createdAt") cmp = a.createdAt.localeCompare(b.createdAt);
      else if (p.sortBy === "status") cmp = Number(a.isActive) - Number(b.isActive);
      else cmp = (a.businessRoleName ?? "").localeCompare(b.businessRoleName ?? "");
      return cmp * dir;
    });

    const total = rows.length;
    const start = (p.page - 1) * p.limit;
    return { items: rows.slice(start, start + p.limit), roleOptions, cameraOptions: [], total };
  }

  const res = await adminFetchUsersList({
    page: p.page,
    limit: p.limit,
    search: p.search,
    roles: p.roles,
    status: p.status,
    dateFilter: p.dateFilter,
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    sortBy: p.sortBy,
    sortDir: p.sortDir,
  });

  let roleOptions: UserRoleOption[] = (res.roleOptions ?? []).map((o) => ({
    id: o.id,
    role: o.role,
    roleId: o.roleId,
    systemRole: o.systemRole,
    slug: o.slug,
    isPredefined: o.isPredefined,
  }));
  if (roleOptions.length === 0) {
    try {
      roleOptions = await adminFetchActiveRoleOptions();
    } catch {
      roleOptions = [];
    }
  }
  const roleNameById = new Map(roleOptions.map((o) => [o.id, o.role]));

  return {
    items: res.items.map((u) => mapApiUserToListItem(u, roleNameById)),
    roleOptions,
    cameraOptions: res.cameraOptions ?? [],
    total: res.total,
  };
}

export type CreateUserBody = {
  firstName: string;
  lastName: string;
  roleId: string;
  email: string;
  password: string;
  cameraIds?: string[];
};

export async function createUser(body: CreateUserBody): Promise<void> {
  await adminCreateUser({
    firstName: body.firstName,
    lastName: body.lastName,
    email: body.email.trim().toLowerCase(),
    password: body.password,
    roleId: body.roleId,
    cameraIds: body.cameraIds,
  });
}

export type UpdateUserBody = {
  firstName: string;
  lastName: string;
  roleId: string;
  email: string;
  password?: string;
  cameraIds?: string[];
};

export async function updateUser(id: string, body: UpdateUserBody): Promise<void> {
  await adminUpdateUser(id, {
    firstName: body.firstName,
    lastName: body.lastName,
    roleId: body.roleId,
    email: body.email.trim().toLowerCase(),
    password: body.password,
    cameraIds: body.cameraIds,
  });
}

export async function fetchUserDetail(id: string): Promise<UserListItem | null> {
  const roleRecords = await adminListRoleRecords();
  const allRoleItems = roleRecords.map((r) => mapApiRoleToListItem(r));
  const { roleNameById } = roleOptionsFromApiRoles(allRoleItems);
  const entity = await adminGetUser(id);
  if (!entity) return null;
  return mapApiUserToListItem(entity, roleNameById);
}

export async function patchUserStatus(id: string, isActive: boolean): Promise<UserListItem> {
  const roleRecords = await adminListRoleRecords();
  const allRoleItems = roleRecords.map((r) => mapApiRoleToListItem(r));
  const { roleNameById } = roleOptionsFromApiRoles(allRoleItems);

  let entity = await adminPatchUserStatus(id, isActive);
  if (!entity) entity = await adminGetUser(id);
  if (!entity) throw new ApiRequestError("User not found.", "NOT_FOUND");
  return mapApiUserToListItem(entity, roleNameById);
}

export async function deleteUser(id: string): Promise<void> {
  await adminDeleteUser(id);
}

export type FetchRolesParams = {
  page: number;
  limit: number;
  search?: string;
  roleIds?: string[];
  status?: "active" | "inactive";
  dateFilter: DateFilterPreset;
  dateFrom?: string;
  dateTo?: string;
  sortBy: "role" | "name" | "createdAt" | "status";
  sortDir: SortDir;
  favoritesOnly?: boolean;
  favoriteRoleIds?: string[];
};

export async function fetchRoles(
  p: FetchRolesParams,
): Promise<{
  items: RoleListItem[];
  roleOptions: { id: string; role: string }[];
  moduleCatalog: ModuleCatalogEntry[];
  total: number;
}> {
  let moduleCatalog: ModuleCatalogEntry[] = resolveModuleCatalog();

  if (p.favoritesOnly) {
    const fav = p.favoriteRoleIds ?? [];
    if (fav.length === 0) return { items: [], roleOptions: [], moduleCatalog, total: 0 };

    const roles = await Promise.all(fav.map((id) => adminGetRole(id)));
    let rows = roles
      .filter((r): r is NonNullable<typeof r> => r != null)
      .map((r) => mapApiRoleToListItem(r, moduleCatalog));
    const roleOptions = rows.map((r) => ({ id: r.id, role: r.role }));

    const q = (p.search ?? "").trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.role.toLowerCase().includes(q) ||
          (r.description ?? "").toLowerCase().includes(q) ||
          r.roleId.toLowerCase().includes(q),
      );
    }
    if (p.roleIds && p.roleIds.length > 0) {
      const set = new Set(p.roleIds);
      rows = rows.filter((r) => set.has(r.id));
    }
    if (p.status === "active") rows = rows.filter((r) => r.isActive);
    if (p.status === "inactive") rows = rows.filter((r) => !r.isActive);
    rows = rows.filter((r) => inDateFilter(r.createdAt, p.dateFilter, p.dateFrom, p.dateTo));

    const dir = p.sortDir === "desc" ? -1 : 1;
    rows.sort((a, b) => {
      let cmp = 0;
      if (p.sortBy === "createdAt") cmp = a.createdAt.localeCompare(b.createdAt);
      else if (p.sortBy === "status") cmp = Number(a.isActive) - Number(b.isActive);
      else cmp = a.role.localeCompare(b.role);
      return cmp * dir;
    });

    const total = rows.length;
    const start = (p.page - 1) * p.limit;
    return { items: rows.slice(start, start + p.limit), roleOptions, moduleCatalog, total };
  }

  const res = await adminFetchRolesList({
    page: p.page,
    limit: p.limit,
    search: p.search,
    roleIds: p.roleIds,
    status: p.status,
    dateFilter: p.dateFilter,
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    sortBy: p.sortBy,
    sortDir: p.sortDir,
  });

  moduleCatalog = resolveModuleCatalog(res.moduleCatalog);
  const items = res.items.map((r) => mapApiRoleToListItem(r, moduleCatalog));
  const roleOptions =
    res.roleOptions?.map((o) => ({ id: o.id, role: o.role })) ?? items.map((r) => ({ id: r.id, role: r.role }));

  return { items, roleOptions, moduleCatalog, total: res.total };
}

export type CreateRoleBody = { role: string; description?: string };

export async function createRole(body: CreateRoleBody): Promise<void> {
  await adminCreateRole(body.role.trim(), body.description?.trim());
}

export async function updateRole(id: string, body: CreateRoleBody): Promise<void> {
  await adminUpdateRole(id, body.role.trim(), body.description?.trim());
}

export async function patchRoleStatus(id: string, isActive: boolean): Promise<RoleListItem> {
  let entity = await adminPatchRoleStatus(id, isActive);
  if (!entity) entity = await adminGetRole(id);
  if (!entity) throw new ApiRequestError("Role not found.", "NOT_FOUND");
  return mapApiRoleToListItem(entity, resolveModuleCatalog());
}

export async function deleteRole(id: string): Promise<void> {
  await adminDeleteRole(id);
}

export async function saveRolePermissions(
  roleId: string,
  payload: SaveRolePermissionsPayload,
  moduleCatalog?: ModuleCatalogEntry[],
): Promise<RoleListItem> {
  const catalog = resolveModuleCatalog(moduleCatalog);
  let entity = await adminReplaceRolePermissions(roleId, payload);
  if (!entity) entity = await adminGetRole(roleId);
  if (!entity) throw new ApiRequestError("Role not found.", "NOT_FOUND");
  return mapApiRoleToListItem(entity, catalog);
}
